import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import { useBodyScrollLock } from "@ui/hooks/useBodyScrollLock";
import classNames from "classnames";

import type { ButtonVariant } from "@jti/core-components/components/Button";
import { Button } from "@jti/core-components/components/Button";

import { PopupVideoDialog } from "./PopupVideoDialog";

import styles from "./PopupVideo.module.scss";

type PopupVideoProps = {
  videoPlayer: ReactNode;
  alignment?: "left" | "right" | "center";
  label: string;
  isInEditor?: boolean;
  color?: "primary" | "secondary" | "tertiary" | "quaternary" | "ghost";
  theme?: "dark" | "light";
};

const buttonVariantMap: Record<NonNullable<PopupVideoProps["color"]>, ButtonVariant> = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "text",
  quaternary: "text-muted",
  ghost: "ghost",
};

export const PopupVideo: FC<PopupVideoProps> = ({
  alignment,
  videoPlayer: VideoPlayer,
  label,
  theme,
  color,
  isInEditor,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const togglePopup = useCallback(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  const { toggleScroll } = useBodyScrollLock();

  useEffect(() => {
    toggleScroll(isPopupOpen);
  }, [isPopupOpen, toggleScroll]);

  return (
    <>
      <div
        className={classNames(styles.buttonWrapper, {
          [styles.center]: alignment === "center",
          [styles.right]: alignment === "right",
        })}
      >
        <Button
          className={theme ? `${theme}-theme` : undefined}
          variant={buttonVariantMap[color ?? "primary"]}
          onClick={togglePopup}
        >
          {label}
        </Button>
      </div>
      {isInEditor ? (
        VideoPlayer
      ) : (
        <PopupVideoDialog videoPlayer={VideoPlayer} handleClose={togglePopup} isPopupOpen={isPopupOpen} />
      )}
    </>
  );
};
